import React, { useState, useRef, useEffect } from 'react';
import { isMobile } from '@/biz/tool';
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import type { StripeAddressElement } from '@stripe/stripe-js';
import { useLoginStore } from '@/hooks/useLoginStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import MessageButton from '../MessageButton';
import {
  TriangleExclamationIcon,
  WarnRedIcon,
  SettingsIcon,
  RightArrowIcon
} from '@/components/common/icon';
import { Alert, Checkbox, Loading } from 'aha-components';
import { Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import styles from './ModalAddress.module.less';
import AddressProgress from './AddressProgress';
import {
  stripePromise,
  validateRFC,
  formatAddressLine,
  getCountryFlag
} from './utils';

const ModalContent: React.FC<{
  autoFocus: boolean;
  handleAddressChange: any;
  addressInfo: any;
  complete: boolean;
  addressElementRef: React.MutableRefObject<StripeAddressElement | null>;
  showRfcValidationRef: React.MutableRefObject<(() => void) | null>;
  addAddressToAccount: boolean;
  setAddAddressToAccount: (value: boolean) => void;
  hasProductSelection: boolean;
}> = ({
  autoFocus,
  handleAddressChange,
  addressInfo,
  complete,
  addressElementRef,
  showRfcValidationRef,
  addAddressToAccount,
  setAddAddressToAccount,
  hasProductSelection
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userAddressInfo } = useLoginStore();
  const { detailData } = useDetailDataStore();
  const [rfcValue, setRfcValue] = useState('');
  const [showRfcError, setShowRfcError] = useState(false);
  const [addressList, setAddressList] = useState<any[]>([]);
  const [formKey, setFormKey] = useState(0);
  const [addressReady, setAddressReady] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [addressElementMounted, setAddressElementMounted] = useState(true);
  const [saveAddressDisabled, setSaveAddressDisabled] = useState(false);
  const [formDefaults, setFormDefaults] = useState<{
    name?: string;
    phone?: string;
    address?: any;
  }>({
    name: undefined,
    phone: undefined,
    address: undefined
  });
  const formLoading = !addressReady || !stripeReady;
  const selectedListAddressRef = useRef<any>(null);
  const remountTokenRef = useRef(0);
  const rfcInputRef = useRef<HTMLInputElement>(null);
  const addressFocusStateRef = useRef({
    country: addressInfo?.country,
    complete,
    rfcValue
  });
  const { setShowAddressModal } = useWebModalStore();

  const getAddressSnapshot = (info: any, nextRfcValue?: string) =>
    JSON.stringify({
      name: info?.name || '',
      phone: info?.phone || '',
      line1: info?.line1 || '',
      line2: info?.line2 || '',
      city: info?.city || '',
      state: info?.state || '',
      postal_code: info?.postal_code || '',
      country: info?.country || '',
      rfcValue: nextRfcValue ?? info?.rfcValue ?? ''
    });

  // 上次地址等内容是否已存在于地址列表
  const findMatchingListAddress = (info: any, list: any[]) =>
    list.find(
      (item: any) =>
        item?.addressInfo &&
        getAddressSnapshot(item.addressInfo) === getAddressSnapshot(info)
    );

  // 使用地址列表地址时禁用“保存到账户”；用户修改后解除禁用
  const syncSaveCheckboxByAddressChange = (
    nextInfo: any,
    nextRfcValue?: string
  ) => {
    if (!selectedListAddressRef.current) return;
    const baseline = getAddressSnapshot(selectedListAddressRef.current);
    const current = getAddressSnapshot(nextInfo, nextRfcValue);
    if (baseline === current) {
      setSaveAddressDisabled(true);
      setAddAddressToAccount(false);
      return;
    }
    setSaveAddressDisabled(false);
  };

  // 将地址信息应用到表单；仅在地址真正变化时才重挂 AddressElement
  const applyAddressToForm = (
    info: any,
    options?: { remount?: boolean; fromAddressList?: boolean }
  ) => {
    if (!info) return;
    const { remount = false, fromAddressList = false } = options || {};
    // 与当前表单内容一致时才跳过重挂（改过再选同一条仍需重挂以回填）
    const sameAsCurrent =
      getAddressSnapshot(addressInfo, rfcValue) === getAddressSnapshot(info);

    setFormDefaults({
      name: info.name,
      phone: info.phone,
      address: info
    });
    setRfcValue(info.rfcValue || '');
    if (fromAddressList) {
      selectedListAddressRef.current = info;
      setSaveAddressDisabled(true);
      setAddAddressToAccount(false);
    } else {
      selectedListAddressRef.current = null;
      setSaveAddressDisabled(false);
    }

    // 表单已是该地址：只同步状态，不重挂 Stripe
    if (remount && !sameAsCurrent) {
      setStripeReady(false);
      setAddressElementMounted(false);
      const token = ++remountTokenRef.current;
      window.setTimeout(() => {
        if (token !== remountTokenRef.current) return;
        setFormKey((key) => key + 1);
        setAddressElementMounted(true);
      }, 50);
    }

    handleAddressChange({
      complete: true,
      value: {
        name: info.name,
        phone: info.phone,
        address: info
      },
      rfcValue: info.rfcValue || ''
    });
  };

  // 获取地址列表，优先用默认地址预填；没有则回退登录态地址
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const res = await axiosInstance.get('/user/shipping-addresses');
        const list = res?.data?.data || [];
        setAddressList(list);
        const defaultItem = list.find((item: any) => item?.isDefault);
        if (defaultItem?.addressInfo) {
          // 使用地址列表默认地址：禁用保存勾选
          applyAddressToForm(defaultItem.addressInfo, {
            fromAddressList: true
          });
        } else if (userAddressInfo) {
          // 上次地址已在列表中：按列表地址处理，禁用保存勾选
          const matchedListItem = findMatchingListAddress(
            userAddressInfo,
            list
          );
          if (matchedListItem?.addressInfo) {
            applyAddressToForm(matchedListItem.addressInfo, {
              fromAddressList: true
            });
          } else {
            // 有上次地址且不在列表：不默认勾选，可编辑
            applyAddressToForm(userAddressInfo);
            setAddAddressToAccount(false);
          }
        } else {
          // 没有默认地址也没有上次地址：默认勾选
          selectedListAddressRef.current = null;
          setSaveAddressDisabled(false);
          setAddAddressToAccount(true);
        }
      } catch (error) {
        setAddressList([]);
        if (userAddressInfo) {
          applyAddressToForm(userAddressInfo);
          setAddAddressToAccount(false);
        } else {
          selectedListAddressRef.current = null;
          setSaveAddressDisabled(false);
          setAddAddressToAccount(true);
        }
      } finally {
        setAddressReady(true);
      }
    };
    fetchAddresses();
    return () => {
      remountTokenRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 地址列表中，选择地址
  const handleSelectAddress = (item: any) => {
    applyAddressToForm(item?.addressInfo, {
      remount: true,
      fromAddressList: true
    });
  };

  // 点击跳转地址管理页
  const goToAddressManagement = () => {
    setShowAddressModal(false);
    navigate('/settings#shippingAddress');
  };

  useEffect(() => {
    addressFocusStateRef.current = {
      country: addressInfo?.country,
      complete,
      rfcValue
    };
  }, [addressInfo?.country, complete, rfcValue]);

  useEffect(() => {
    showRfcValidationRef.current = () => {
      if (addressInfo?.country === 'MX' && !validateRFC(rfcValue)) {
        setShowRfcError(true);
      }
    };
    return () => {
      showRfcValidationRef.current = null;
    };
  }, [addressInfo?.country, rfcValue, showRfcValidationRef]);

  useEffect(() => {
    if (!autoFocus) return;

    const timer = window.setTimeout(() => {
      const {
        country,
        complete: isComplete,
        rfcValue: currentRfcValue
      } = addressFocusStateRef.current;
      if (isComplete && country === 'MX' && !currentRfcValue) {
        rfcInputRef.current?.focus();
        return;
      }

      addressElementRef.current?.focus();
    }, 50);

    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  // 值变化校验
  const handleRfcChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const oldCursorPosition = input.selectionStart || 0;

    // 移除所有空格，保持光标位置
    const newValue = input.value.toUpperCase().replace(/\s/g, '');

    // 计算移除的空格数量（光标之前的空格）
    const spacesBeforeCursor = input.value
      .substring(0, oldCursorPosition)
      .replace(/\S/g, '').length;
    const newCursorPosition = Math.max(
      0,
      oldCursorPosition - spacesBeforeCursor
    );

    setRfcValue(newValue);
    const info = addressInfo || {
      ...formDefaults.address,
      name: formDefaults.name,
      phone: formDefaults.phone
    };
    const { name, phone, ...address } = info || {};
    handleAddressChange({
      ...{ value: { name, address, phone } },
      rfcValue: newValue,
      complete
    });
    syncSaveCheckboxByAddressChange({ ...address, name, phone }, newValue);
    if (newValue && !validateRFC(newValue)) {
      setShowRfcError(true);
    } else {
      setShowRfcError(false);
    }

    // 恢复光标位置
    setTimeout(() => {
      if (rfcInputRef.current) {
        rfcInputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);
  };

  return (
    <div className={isMobile() ? styles.mobileContent : styles.content}>
      <div className={styles.scrollBody}>
        <AddressProgress
          step={hasProductSelection ? 3 : 2}
          hasProductSelection={hasProductSelection}
        />
        <div className="flex items-center justify-between">
          <div className="text-base font-medium leading-6 mb-3">
            {t('modal.address.title', {
              defaultValue: 'Enter your address details'
            })}
          </div>
          <MessageButton order={detailData?.order} timUnreadCount={0} isText />
        </div>
        <div className={styles.desc}>
          <span>
            {t('modal.address.description', {
              defaultValue:
                'Please enter your address to receive the product from the brand. Your address information is secure and protected, and it will only be used for the purpose of shipping the product to you.'
            })}
          </span>
        </div>
        <Alert
          type="warning"
          showIcon
          closeable
          className="mb-4"
          description={t('modal.address.local_language_hint', {
            defaultValue:
              'Please enter the recipient’s name and full address in the local language of the destination country or region to help ensure accurate delivery'
          })}
        />
        <div className="mb-2 flex justify-between items-center">
          <div className="justify-start text-text-color-primary text-base font-semibold font-['Inter'] leading-6">
            {t('modal.address.shipping_address_label', {
              defaultValue: 'Shipping address'
            })}
          </div>
          <div>
            <Dropdown
              getPopupContainer={() => document.body}
              overlayClassName="z-[2000]"
              dropdownRender={() => (
                <div className="w-[460px] max-w-[calc(100vw-48px)] bg-white rounded-xl border border-solid border-[#E7E5E4] shadow-[0_8px_24px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden">
                  <div className="max-h-[320px] overflow-y-auto">
                    {addressList.map((item: any, index: number) => {
                      const info = item?.addressInfo || {};
                      const flag = getCountryFlag(info.country);
                      return (
                        <div
                          key={item?.id ?? index}
                          className={`px-4 py-3 cursor-pointer hover:bg-[#FAFAF9] ${
                            index < addressList.length - 1
                              ? 'border-b border-solid border-[#E7E5E4]'
                              : ''
                          }`}
                          onClick={() => handleSelectAddress(item)}
                        >
                          <div className="text-sm font-medium text-[#171717] leading-5 mb-2 break-words">
                            {formatAddressLine(info)}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap text-sm text-[#525252]">
                            <span className="font-normal leading-5">
                              {info.name}
                            </span>
                            {info.phone && (
                              <>
                                <div className="w-[1px] h-3.5 bg-[#E7E5E4]" />
                                <div className="inline-flex items-center gap-1 border border-solid border-[#D4D4D4] px-2 py-0.5 text-xs rounded-[6px] leading-5 text-[#525252]">
                                  {flag && (
                                    <span className="text-[12px] leading-none">
                                      {flag}
                                    </span>
                                  )}
                                  <span>{info.phone}</span>
                                </div>
                              </>
                            )}
                            {item?.isDefault && (
                              <div className="px-2 py-0.5 bg-[#FFF7ED] rounded-full border border-solid border-[#FED7AA] inline-flex items-center">
                                <span className="text-[#C2410C] text-xs font-medium font-['Inter'] leading-4">
                                  {t('settings.shipping_address.default', {
                                    defaultValue: 'Default'
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    className="flex-shrink-0 flex items-center justify-center gap-1.5 py-3 border-t border-solid border-[#E7E5E4] cursor-pointer text-[#FF7728] text-sm font-medium hover:bg-[#FFF7ED]"
                    onClick={goToAddressManagement}
                  >
                    <SettingsIcon className="w-4 h-4 text-[#FF7728]" />
                    <span>
                      {t('modal.address.address_management', {
                        defaultValue: 'Address management'
                      })}
                    </span>
                  </div>
                </div>
              )}
            >
              <a
                className="text-sm font-medium text-[#FF621D] cursor-pointer inline-flex items-center gap-1"
                onClick={(e) => e.preventDefault()}
              >
                {t('modal.address.select_address', {
                  defaultValue: 'Select address'
                })}
                <RightArrowIcon className="w-5 h-5 rotate-90" />
              </a>
            </Dropdown>
          </div>
        </div>
        <div className="p-4 bg-[#F5F5F5] rounded-lg relative min-h-[320px]">
          {formLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-[#F5F5F5]">
              <Loading width={48} height={48} color="#FB2C36" />
            </div>
          )}
          <div className={formLoading ? 'invisible' : undefined}>
            <div
              className={`${styles.stripeElements} ${isMobile() ? 'w-[calc(100%-4px)]' : ''}`}
            >
              <Elements
                stripe={stripePromise}
                options={{
                  appearance: {
                    variables: {
                      fontFamily: "'Inter'",
                      fontWeightNormal: '600'
                    },
                    rules: {
                      '.Label': {
                        fontFamily: "'Inter'",
                        color: '#57534D',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        opacity: '1'
                      }
                    }
                  }
                }}
              >
                {addressReady && addressElementMounted && (
                  <AddressElement
                    key={formKey}
                    options={{
                      mode: 'billing',
                      fields: {
                        phone: 'always'
                      },
                      validation: {
                        phone: {
                          required: 'always'
                        }
                      },
                      defaultValues: {
                        name: formDefaults.name,
                        address: formDefaults.address,
                        phone: formDefaults.phone
                      }
                    }}
                    onReady={(element) => {
                      addressElementRef.current = element;
                      setStripeReady(true);
                      if (
                        autoFocus &&
                        !(complete && addressInfo?.country === 'MX')
                      ) {
                        window.setTimeout(() => element.focus(), 0);
                      }
                    }}
                    onChange={(event) => {
                      const nextRfcValue =
                        rfcValue || formDefaults.address?.rfcValue || '';
                      handleAddressChange({
                        ...event,
                        rfcValue: nextRfcValue
                      });
                      const { address, name, phone } = event?.value || {};
                      syncSaveCheckboxByAddressChange(
                        { ...address, name, phone },
                        nextRfcValue
                      );
                    }}
                  />
                )}
              </Elements>
            </div>
            {addressInfo?.country === 'MX' && (
              <div className="flex flex-col gap-2 mt-4 w-full">
                <div className="justify-start">
                  <span className="text-[#57534D] text-[12px] font-['Inter'] leading-5 font-[500]">
                    {t('modal.address.rfc.label', {
                      defaultValue: 'Tax Identification Number (RFC)'
                    })}
                  </span>
                  <span className="text-[#DC2626] text-sm font-['Inter'] leading-5">
                    *
                  </span>
                </div>
                <div className="w-full">
                  <div
                    className={`h-10 max-h-10 min-h-10 px-3 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] ${
                      showRfcError ? 'outline-[#DC2626]' : 'outline-zinc-200'
                    } flex justify-end items-center gap-16`}
                  >
                    <input
                      ref={rfcInputRef}
                      className="flex-1 w-full text-start text-[#292524] text-sm font-medium leading-5 bg-transparent border-none outline-none placeholder:text-[#A6A09B]"
                      value={rfcValue}
                      onChange={handleRfcChange}
                      placeholder={t('modal.address.rfc.placeholder', {
                        defaultValue: 'e.g. GODE561231GR8'
                      })}
                      type="text"
                    />
                  </div>
                  {showRfcError && (
                    <div className="px-4 py-0.5 bg-[#FEE2E2] rounded flex justify-center items-start gap-1 mt-[10px]">
                      <WarnRedIcon className="w-3 h-3 mt-1" />
                      <div className="flex-1 justify-start text-[#DC2626] text-xs font-normal font-['Inter'] leading-5">
                        {t('modal.address.rfc.error_message', {
                          defaultValue:
                            'Invalid RFC format. Your RFC must contain 13 characters: 4 letters, 6 digits for your birth date (YYMMDD), and 3 alphanumeric characters.'
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              className={`self-stretch inline-flex justify-start items-start gap-2 mt-4 ${
                saveAddressDisabled ? 'opacity-50' : ''
              }`}
            >
              <Checkbox
                checked={addAddressToAccount}
                disabled={saveAddressDisabled}
                onChange={() => {
                  if (saveAddressDisabled) return;
                  setAddAddressToAccount(!addAddressToAccount);
                }}
              />
              <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                <div className="justify-start text-[#171717] text-sm font-medium font-['Inter'] leading-5">
                  {t('modal.address.save_address_to_account', {
                    defaultValue:
                      'Save this address to my account for future use'
                  })}
                </div>
                <div className="self-stretch justify-start text-xs font-normal leading-4">
                  {t('modal.address.save_address_to_account_description', {
                    defaultValue:
                      "You won't need to type it again for your next collaboration. Manage saved addresses anytime in "
                  })}
                  <span
                    className="text-[#FF621D] cursor-pointer underline"
                    onClick={goToAddressManagement}
                  >
                    {t('modal.address.account_settings', {
                      defaultValue: 'Account Settings'
                    })}
                  </span>
                  .
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalContent;
